import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useRef } from 'react';

import { THREE_SECONDS } from '@/constants/time';
import {
  type FeatureRealtimeHandler,
  type RoutedRealtimeEvent,
  routeRealtimeEvent,
} from '@/hooks/realtime/featureRealtime';
import { FEATURE_REALTIME_HANDLERS } from '@/hooks/realtime/featureRealtimeHandlers';
import { useRealtimeChannel } from '@/hooks/realtime/useRealtimeChannel';
import { useAppResume } from '@/hooks/useAppResume';
import { userIdAtom } from '@/services/atoms/auth';
import { invalidateInboxDebounced } from '@/services/conversations/cache';
import { emitInboxSignal } from '@/services/conversations/inboxSignals';
import { conversationKeys } from '@/services/conversations/keys';
import {
  inboxChannelFor,
  parseInboxRealtimeEvent,
  type InboxRealtimeEvent,
} from '@/services/conversations/realtimeEvents';
import { getRealtimeToken, realtimeTokenUserId } from '@/services/conversations/realtimeToken';
import { lastStoredIndex, type SupportThread } from '@/services/conversations/thread';
import { useInboxRealtimeStore } from '@/services/stores/inboxRealtimeStore';
import { invalidateUnreadInquiriesDebounced } from '@/util/inboxInvalidation';
import { Sentry } from '@/util/sentry';

/** Resume signals closer together than this are one refresh. */
const INBOX_REFRESH_WINDOW = THREE_SECONDS;

const INBOX_REALTIME_MONITORING = {
  breadcrumbCategory: 'inbox.realtime',
  refusedMessage: 'Inbox realtime subscription refused',
  component: 'InboxLiveUpdates',
  source: 'inbox-realtime',
};

/**
 * Applies one hint to the cache. A hint never supplies content: it decides
 * what is re-read.
 *
 * A new message re-reads its thread only when the thread is cached and the
 * cache does not already hold that index, which is the case for this device's
 * own sends. The inbox list and unread are debounced, because the Primum
 * Support account receives a hint for every member's reply.
 *
 * A changed message moves neither the inbox nor unread, so it touches no
 * cache here; an open inquiry chat hears it through the inbox signals.
 */
export function applyInboxEvent(queryClient: QueryClient, event: InboxRealtimeEvent): void {
  if (event.type === 'message.updated') {
    return;
  }

  if (event.type === 'message.created') {
    const key = conversationKeys.thread(event.data.conversationId);

    const cached = queryClient.getQueryData<SupportThread>(key);

    if (cached && event.data.index > lastStoredIndex(cached)) {
      queryClient.invalidateQueries({ queryKey: key, exact: true }).catch(() => undefined);
    }

    invalidateUnreadInquiriesDebounced(queryClient);
  }

  if (event.type === 'conversation.read') {
    invalidateUnreadInquiriesDebounced(queryClient);
  }

  invalidateInboxDebounced(queryClient);
}

/**
 * Runs a feature's handler so that one feature's bug cannot stop the inbox,
 * or another feature, from following the channel. A throw is reported and
 * the event dropped.
 */
function runFeatureHandler(prefix: string, run: () => void): void {
  try {
    run();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: INBOX_REALTIME_MONITORING.component, source: 'feature-realtime' },
      extra: { errorMessage: error instanceof Error ? error.message : undefined, prefix },
    });
  }
}

/**
 * Applies one routed event: an inbox hint to the inbox caches, a feature's
 * hint to that feature's handler.
 */
export function applyRealtimeEvent(
  queryClient: QueryClient,
  routed: RoutedRealtimeEvent<InboxRealtimeEvent>
): void {
  if (routed.kind === 'inbox') {
    applyInboxEvent(queryClient, routed.event);

    return;
  }

  runFeatureHandler(routed.handler.prefix, () => routed.handler.apply(queryClient, routed.event));
}

/** Every feature's resume, each isolated like its events. */
export function resumeFeatures(
  queryClient: QueryClient,
  handlers: readonly FeatureRealtimeHandler[] = FEATURE_REALTIME_HANDLERS
): void {
  for (const handler of handlers) {
    runFeatureHandler(handler.prefix, () => handler.onResume?.(queryClient));
  }
}

function parseRealtimeEvent(raw: unknown): RoutedRealtimeEvent<InboxRealtimeEvent> {
  return routeRealtimeEvent(raw, FEATURE_REALTIME_HANDLERS, parseInboxRealtimeEvent);
}

function channelFor(token: string): string | null {
  const userId = realtimeTokenUserId(token);

  return userId ? inboxChannelFor(userId) : null;
}

/**
 * Follows the effective user's inbox channel for as long as they are signed
 * in, so the tab badge, the inbox and an open support thread move when a
 * message arrives on any screen.
 *
 * App-wide rather than scoped to the inbox, unlike Ask's socket, because the
 * badge is visible everywhere. Keyed on the effective user, so starting or
 * stopping acting as another account follows that account's channel.
 *
 * The channel keeps no history, so a reconnect, a return to the foreground and
 * the network coming back all mean the same thing, and share one refresh of
 * the inbox, unread, any thread on screen and every feature's `onResume`.
 *
 * Features ride the same channel: an event whose type carries a feature's
 * prefix goes to that feature's handler (see `FeatureRealtimeHandler`).
 */
export function useInboxLiveUpdates(): void {
  const queryClient = useQueryClient();

  const userId = useAtomValue(userIdAtom);

  const lastRefreshAt = useRef(0);

  const refresh = useCallback(() => {
    const now = Date.now();

    if (now - lastRefreshAt.current < INBOX_REFRESH_WINDOW) {
      return;
    }

    lastRefreshAt.current = now;

    invalidateInboxDebounced(queryClient, 0);

    invalidateUnreadInquiriesDebounced(queryClient, 0);

    queryClient
      .invalidateQueries({ queryKey: conversationKeys.threads, refetchType: 'active' })
      .catch(() => undefined);

    emitInboxSignal({ type: 'resync' });

    resumeFeatures(queryClient);
  }, [queryClient]);

  useAppResume(refresh);

  useRealtimeChannel<RoutedRealtimeEvent<InboxRealtimeEvent>>({
    enabled: Boolean(userId),
    identity: userId,
    getToken: getRealtimeToken,
    channelFor,
    parse: parseRealtimeEvent,
    onEvent: (routed) => {
      applyRealtimeEvent(queryClient, routed);

      if (routed.kind === 'inbox') {
        emitInboxSignal(routed.event);
      }
    },
    onResume: refresh,
    onConnectionChange: useInboxRealtimeStore.getState().setConnection,
    monitoring: INBOX_REALTIME_MONITORING,
  });
}
